import { useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { aedStr, ilsStr, eurStr, usdStr, pct, round, areaStr } from '../lib/analysisEngine';
import { compact } from '../lib/uiFormat';
import { useI18n } from '../lib/i18n';

const PALETTE = ['#1D9E75', '#1E3A5F', '#B98A3D'];   // brand green / navy / gold — one unit each

function CmpTooltip({ active, payload, label, ils, eur, usd, kind }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs min-w-[150px]">
      <p className="font-semibold text-gray-700 mb-1.5">{label}</p>
      {payload.filter(p => p.value != null).map((p, i) => (
        <div key={i} className="flex items-center gap-2 mb-1 last:mb-0">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color || p.stroke }} />
          <span className="text-gray-500 truncate">{p.name}</span>
          <span className="ml-auto text-right leading-tight">
            {kind === 'pct'
              ? <span className="font-medium text-gray-800">{pct(p.value)}</span>
              : <><span className="font-medium text-gray-800 block">{aedStr(p.value)}</span><span className="text-[10px] text-gray-400 block">{ilsStr(p.value * ils)}</span>{eur != null && <span className="text-[10px] text-gray-400 block">{eurStr(p.value * eur)}</span>}{usd != null && <span className="text-[10px] text-gray-400 block">{usdStr(p.value * usd)}</span>}</>}
          </span>
        </div>
      ))}
    </div>
  );
}

// `k` is the i18n dictionary key for the row label (translated at render time).
// BASE_ROWS never depend on rental strategy (price, area, fees, appreciation-driven
// value) — always sourced from the LTR analysis object, shown regardless of strategy.
const BASE_ROWS = [
  { k: 'cmp_price', get: a => a.price, fmt: 'aed', best: 'min' },
  { k: 'cmp_internal_area', get: a => a.internal, fmt: 'sqft', best: 'max' },
  { k: 'cmp_price_per_sqft', get: a => a.pricePerSqft, fmt: 'aed', best: 'min' },
  { k: 'cmp_discount_market', get: a => (a.hasMarket ? a.discountPct : undefined), fmt: 'pct', best: 'max' },
  { k: 'cmp_paid_until_handover', get: a => a.bucketUntil, fmt: 'aed', best: 'min' },
  { k: 'on_handover', get: a => a.bucketOnHandover, fmt: 'aed', best: 'min' },
  { k: 'post_handover_phpp', get: a => (a.hasPhpp ? a.bucketPost : undefined), fmt: 'aed', best: 'max' },
  { k: 'cmp_total_incl_fees', get: a => a.totalCost, fmt: 'aed', best: 'min' },
  { k: 'cmp_dld_admin_fees', get: a => a.totalFees, fmt: 'aed', best: 'min' },
  { k: 'cmp_service_charge_yr', get: a => a.scYearly, fmt: 'aed', best: 'min' },
  { k: 'expected_rent_yr', get: a => a.startRent, fmt: 'aed', best: 'max' },
  { k: 'cmp_value_handover', get: a => a.atHandover?.value, fmt: 'aed', best: 'max' },
  { k: 'cmp_value_midpoint', get: a => a.atMid?.value, fmt: 'aed', best: 'max' },
  { k: 'cmp_value_maturity', get: a => a.atMaturity?.value, fmt: 'aed', best: 'max' },
];

// STR-only metrics — no LTR equivalent, so no "dual" row needed; always read off the
// STR analysis object (a.str is computed on every analysis regardless of its own
// strategy, but we always source these from analysisStr for clarity/consistency).
const STR_ROWS = [
  { k: 'cmp_str_adr', get: a => a.str?.adr, fmt: 'aed', best: null },
  { k: 'cmp_str_occupancy', get: a => a.str?.occupancy, fmt: 'pct', best: null },
  { k: 'gross_revenue_yr', get: a => a.str?.gross, fmt: 'aed', best: 'max' },
  { k: 'total_expenses_yr', get: a => a.str?.totalExpenses, fmt: 'aed', best: 'min' },
  { k: 'str_net_income_yr', get: a => a.str?.net, fmt: 'aed', best: 'max' },
  { k: 'str_net_yield', get: a => a.str?.netYield, fmt: 'pct', best: 'max' },
];

// DUAL_ROWS differ between LTR and STR (net income feeds yields/ROI/break-even) — the
// same row swaps to STR figures in "str" mode, or renders twice (LTR + STR sub-rows) in
// "compare" mode, same as the STR-only rows above and the exit rows below.
const DUAL_ROWS = [
  { k: 'gross_yield_rp', get: a => a.grossYield, fmt: 'pct', best: 'max' },
  { k: 'net_yield_nc', get: a => a.netYield, fmt: 'pct', best: 'max' },
  { k: 'coc_yield', get: a => a.cashYield, fmt: 'pct', best: 'max' },
  { k: 'cmp_capital_roi_handover', get: a => a.roiAt(a.atHandover), fmt: 'pct', best: 'max' },
  { k: 'cmp_flip_handover', get: a => a.flipROI, fmt: 'pct', best: 'max' },
  { k: 'cmp_capital_roi_maturity', get: a => a.roiAt(a.atMaturity), fmt: 'pct', best: 'max' },
  { k: 'cmp_combined_roi_ann', get: a => a.combinedMaturityAnnual, fmt: 'pct', best: 'max' },
  { k: 'cmp_break_even_year', get: a => (a.breakEven ? a.breakEven.year : Infinity), fmt: 'year', best: 'min' },
];

// Exit-scenario rows depend on the selected holding period (n years after handover) AND
// the strategy (STR net rent/opex vs LTR) — also a DUAL set.
const exitScn = (a, n) => {
  const arr = a.exitScenarios || [];
  return arr.length ? arr[Math.min(n, arr.length - 1)] : {};
};
const exitRows = (n) => [
  { k: 'cmp_exit_sale_value', get: a => exitScn(a, n).saleValue, fmt: 'aed', best: 'max' },
  { k: 'cmp_exit_outstanding', get: a => exitScn(a, n).outstanding, fmt: 'aed', best: 'min' },
  { k: 'cmp_exit_payments_fees', get: a => exitScn(a, n).cashInvested, fmt: 'aed', best: 'min' },
  { k: 'cmp_exit_profit', get: a => exitScn(a, n).profit, fmt: 'aed', best: 'max' },
  { k: 'cmp_exit_net_after', get: a => exitScn(a, n).netAfter, fmt: 'aed', best: 'max' },
  { k: 'cmp_exit_roi', get: a => exitScn(a, n).roi, fmt: 'pct', best: 'max' },
  { k: 'cmp_exit_ann_roi', get: a => exitScn(a, n).annualizedRoi, fmt: 'pct', best: 'max' },
];

// Assemble the final row list for the active strategy. `pick(e)` resolves which analysis
// object (LTR or STR) a row instance reads from; `badge` marks compare-mode sub-rows.
function buildRows(strategy, n) {
  const dual = [...DUAL_ROWS, ...exitRows(n)];
  const rows = BASE_ROWS.map(r => ({ ...r, pick: e => e.analysis }));
  if (strategy === 'ltr') {
    rows.push(...dual.map(r => ({ ...r, pick: e => e.analysis })));
  } else if (strategy === 'str') {
    rows.push(...STR_ROWS.map(r => ({ ...r, pick: e => e.analysisStr })));
    rows.push(...dual.map(r => ({ ...r, pick: e => e.analysisStr })));
  } else {
    rows.push(...STR_ROWS.map(r => ({ ...r, pick: e => e.analysisStr })));
    dual.forEach(r => {
      rows.push({ ...r, pick: e => e.analysis, badge: 'ltr_short' });
      rows.push({ ...r, pick: e => e.analysisStr, badge: 'str_short' });
    });
  }
  return rows;
}

// Numeric/currency cells stay LTR even inside the RTL page — tabular-nums is
// isolated to direction:ltr by the RTL CSS, so wrap every numeric span in it.
function fmtVal(v, fmt, ils, eur, usd, t) {
  if (v === undefined || v === null || (typeof v === 'number' && !isFinite(v)))
    return fmt === 'year' ? <span className="tabular-nums">{t('gt_x_yr', { x: 10 })}</span> : '—';
  if (fmt === 'aed') return (
    <span className="tabular-nums">{aedStr(v)}<span className="block text-xs text-gray-400">{ilsStr(v * ils)}</span>{eur != null && <span className="block text-xs text-gray-400">{eurStr(v * eur)}</span>}{usd != null && <span className="block text-xs text-gray-400">{usdStr(v * usd)}</span>}</span>
  );
  if (fmt === 'pct') return <span className="tabular-nums">{pct(v)}</span>;
  if (fmt === 'sqft') return <span className="tabular-nums">{areaStr(v)}</span>;
  if (fmt === 'year') return <span className="tabular-nums">{t('yr_x', { x: v })}</span>;
  return String(v);
}

// entries: [{ label, sub, project, unit, analysis, analysisStr, hasStr }]
// strategy: 'ltr' | 'str' | 'compare' — applies to the comparison as a whole, same as Single mode.
export default function ComparisonReport({ entries, ils, eur, usd, strategy = 'ltr' }) {
  const { t, dir } = useI18n();
  const [exitN, setExitN] = useState(0);
  if (!entries || entries.length < 2) return null;
  const showStr = strategy !== 'ltr';
  const showBoth = strategy === 'compare';

  // max selectable holding period = shortest horizon across the compared units
  const maxN = Math.min(...entries.map(e => Math.max(0, (e.analysis.exitScenarios || []).length - 1)));
  const n = Math.min(exitN, maxN);
  const rows = buildRows(strategy, n);

  const bestIndexForRow = (row) => {
    if (!row.best) return -1;
    const vals = entries.map(e => row.get(row.pick(e)));
    const finite = vals.map((v, i) => ({ v, i })).filter(x => typeof x.v === 'number' && isFinite(x.v));
    if (!finite.length) return -1;
    const target = row.best === 'min'
      ? Math.min(...finite.map(x => x.v)) : Math.max(...finite.map(x => x.v));
    // if all equal, don't highlight
    if (finite.every(x => Math.abs(x.v - finite[0].v) < 1e-6)) return -1;
    return finite.find(x => Math.abs(x.v - target) < 1e-6)?.i ?? -1;
  };

  // merged projection datasets keyed by calendar year
  const allYears = Array.from(new Set(entries.flatMap(e => e.analysis.years.map(y => y.year)))).sort((a, b) => a - b);
  const valueData = allYears.map(year => {
    const row = { year };
    entries.forEach((e, i) => {
      const y = e.analysis.years.find(x => x.year === year);
      if (y) row['v' + i] = round(y.value);
    });
    return row;
  });
  // ROI + net-income lines follow the active strategy: 'ltr'/'str' plot one series per
  // unit from the matching analysis object; 'compare' plots both (solid = LTR, dashed = STR).
  const roiData = allYears.map(year => {
    const row = { year };
    entries.forEach((e, i) => {
      const y = e.analysis.years.find(x => x.year === year);
      if (y && (strategy !== 'str')) row['r' + i] = Number(y.combinedROI.toFixed(1));
      const ys = e.analysisStr?.years.find(x => x.year === year);
      if (ys && showStr) row['rs' + i] = Number(ys.combinedROI.toFixed(1));
    });
    return row;
  });
  const netIncomeData = allYears.map(year => {
    const row = { year };
    entries.forEach((e, i) => {
      const y = e.analysis.years.find(x => x.year === year);
      if (y && y.ysh >= 1 && strategy !== 'str') row['n' + i] = round(y.netRent);
      const ys = e.analysisStr?.years.find(x => x.year === year);
      if (ys && ys.ysh >= 1 && showStr) row['ns' + i] = round(ys.netRent);
    });
    return row;
  });

  return (
    <div className="space-y-6">
      {/* comparison table */}
      <div className="bg-white rounded-[1.75rem] p-5 shadow-premium border border-black/[0.06]">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-semibold text-gray-800">{t('cmp_side_by_side')}</h3>
          <label className="flex items-center gap-2 text-xs text-gray-500">
            {t('exit_after')}
            <select value={n} onChange={e => setExitN(Number(e.target.value))}
              className="border border-gray-200 rounded-lg px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500">
              {Array.from({ length: maxN + 1 }, (_, i) => (
                <option key={i} value={i}>{i === 0 ? t('exit_handover') : t('plus_x_yr', { x: i })}</option>
              ))}
            </select>
            <span className="text-gray-400">{t('cmp_applied_all_units')}</span>
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="text-sm border-collapse min-w-[560px]">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-white text-left py-2 pr-4 font-medium text-gray-400 border-b border-black/[0.06]">{t('metric')}</th>
                {entries.map((e, i) => (
                  <th key={i} className="py-2 px-4 text-right font-semibold text-gray-800 border-b border-black/[0.06] min-w-[150px]">
                    <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: PALETTE[i] }}></span>
                    {e.label}
                    {e.project?.is_phpp && <span className="ml-1 text-[9px] bg-blue-100 text-blue-700 px-1 py-0.5 rounded-full align-middle" title={t('phpp_title')}>PHPP</span>}
                    {showStr && !e.hasStr && (
                      <span className="ml-1 text-[9px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded-full align-middle" title={t('cmp_no_str_data_title')}>{t('cmp_no_str_data')}</span>
                    )}
                    <span className="block text-xs font-normal text-gray-400 tabular-nums">{e.sub}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => {
                const best = bestIndexForRow(row);
                return (
                  <tr key={ri} className={`border-b border-gray-50 ${ri % 2 ? 'bg-gray-50/40' : ''}`}>
                    <td className="sticky left-0 z-10 bg-white py-2 pr-4 text-gray-500 whitespace-nowrap">
                      {t(row.k)}
                      {row.badge && <span className={`ml-1.5 text-[9px] px-1 py-0.5 rounded-full align-middle ${row.badge === 'str_short' ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-500'}`}>{t(row.badge)}</span>}
                    </td>
                    {entries.map((e, i) => (
                      <td key={i} className={`py-2 px-4 text-right ${best === i ? 'bg-green-50 text-green-700 font-semibold rounded' : 'text-gray-800'}`}>
                        {fmtVal(row.get(row.pick(e)), row.fmt, ils, eur, usd, t)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-400 mt-3">{t('cmp_best_value_note')}</p>
      </div>

      {/* combined charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-[1.75rem] p-5 shadow-premium border border-black/[0.06]">
          <h3 className="font-semibold text-gray-800 mb-3 text-sm">{t('cmp_value_projection')}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={valueData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={compact} width={44} />
              <Tooltip content={<CmpTooltip ils={ils} eur={eur} usd={usd} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} align={dir === 'rtl' ? 'right' : 'left'} />
              {entries.map((e, i) => (
                <Line key={i} type="monotone" dataKey={'v' + i} name={e.label} stroke={PALETTE[i]} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-[1.75rem] p-5 shadow-premium border border-black/[0.06]">
          <h3 className="font-semibold text-gray-800 mb-3 text-sm">{t('combined_roi_over_time')}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={roiData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v.toFixed(0) + '%'} width={44} />
              <Tooltip content={<CmpTooltip kind="pct" />} />
              <Legend wrapperStyle={{ fontSize: 12 }} align={dir === 'rtl' ? 'right' : 'left'} />
              {entries.map((e, i) => (strategy !== 'str') && (
                <Line key={'r' + i} type="monotone" dataKey={'r' + i} name={showBoth ? `${e.label} (${t('ltr_short')})` : e.label} stroke={PALETTE[i]} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
              ))}
              {entries.map((e, i) => showStr && (
                <Line key={'rs' + i} type="monotone" dataKey={'rs' + i} name={showBoth ? `${e.label} (${t('str_short')})` : e.label} stroke={PALETTE[i]} strokeDasharray={showBoth ? '5 3' : undefined} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        {showStr && (
          <div className="bg-white rounded-[1.75rem] p-5 shadow-premium border border-black/[0.06]">
            <h3 className="font-semibold text-gray-800 mb-3 text-sm">{t('cmp_net_income_over_time')}</h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={netIncomeData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={compact} width={44} />
                <Tooltip content={<CmpTooltip ils={ils} eur={eur} usd={usd} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} align={dir === 'rtl' ? 'right' : 'left'} />
                {showBoth && entries.map((e, i) => (
                  <Line key={'n' + i} type="monotone" dataKey={'n' + i} name={`${e.label} (${t('ltr_short')})`} stroke={PALETTE[i]} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                ))}
                {entries.map((e, i) => (
                  <Line key={'ns' + i} type="monotone" dataKey={'ns' + i} name={showBoth ? `${e.label} (${t('str_short')})` : e.label} stroke={PALETTE[i]} strokeDasharray={showBoth ? '5 3' : undefined} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
